import { useEffect, useState, type PropsWithChildren } from 'react';
import { AppRoot } from '@telegram-apps/telegram-ui';

/**
 * Получить текущую цветовую схему из Telegram WebApp API
 */
function getTgColorScheme(): 'light' | 'dark' {
  try {
    const tgWebApp = (window as any).Telegram?.WebApp;
    if (tgWebApp?.colorScheme === 'dark') return 'dark';
  } catch {
    // fallback
  }
  return 'light';
}

/**
 * Внутренний провайдер — инициализирует Mini App при загрузке
 * Использует нативный Telegram WebApp API напрямую
 */
function MiniAppInit({ children }: PropsWithChildren) {
  useEffect(() => {
    try {
      const tgWebApp = (window as any).Telegram?.WebApp;
      if (tgWebApp) {
        // Сообщаем Telegram, что приложение готово
        tgWebApp.ready();
        // Раскрываем на весь экран
        tgWebApp.expand();
      }
    } catch {
      // Вне Telegram — игнорируем
    }
  }, []);

  return <>{children}</>;
}

/**
 * Корневой провайдер — оборачивает приложение в Telegram UI тему
 * Автоматически определяет тёмную/светлую тему из Telegram и реагирует на переключение
 */
export function Providers({ children }: PropsWithChildren) {
  const [colorScheme, setColorScheme] = useState<'light' | 'dark'>(getTgColorScheme);

  // Определяем платформу для стилей
  let platform: 'ios' | 'base' = 'base';
  try {
    const tgWebApp = (window as any).Telegram?.WebApp;
    const tgPlatform = tgWebApp?.platform || '';
    if (['macos', 'ios'].includes(tgPlatform)) {
      platform = 'ios';
    }
  } catch {
    // fallback
  }

  // Слушаем смену темы в реальном времени
  useEffect(() => {
    try {
      const tgWebApp = (window as any).Telegram?.WebApp;
      if (tgWebApp?.onEvent) {
        const handler = () => {
          setColorScheme(getTgColorScheme());
        };
        tgWebApp.onEvent('themeChanged', handler);
        return () => {
          tgWebApp.offEvent('themeChanged', handler);
        };
      }
    } catch {
      // fallback
    }
  }, []);

  return (
    <AppRoot appearance={colorScheme} platform={platform}>
      <MiniAppInit>
        {children}
      </MiniAppInit>
    </AppRoot>
  );
}
